import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import {
  Stack,
  Typography,
  Box,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  styled,
  Divider,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LocalizationProvider,
  DatePicker,
  DesktopTimePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import api from '../api/axios';
import { updateDetails } from '../redux/detailsSlice';
import { useDispatch } from 'react-redux';
import SettingImage from '../assets/images/AccountSettings.svg';
import CustomLocationAutocomplete from '../components/LocationPicker';
import {
  AstroDetailsFormStyles,
  StyledButton,
} from '../components/StyledComponents';
import BreadcrumbNavbar from '../components/BreadcrumbNavbar';
import { useNavigate } from 'react-router-dom';
import ProcessingScreen from '../components/ProcessingScreen';

const StyledTextField = styled(TextField)(AstroDetailsFormStyles);
const StyledDatePicker = styled(DatePicker)(AstroDetailsFormStyles);
const StyledTimePicker = styled(DesktopTimePicker)(AstroDetailsFormStyles);

const SettingsForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    dob: '',
    timeOfBirth: '',
    placeOfBirth: '',
  });

  const [activeTab, setActiveTab] = useState(0);

  const dispatch = useDispatch();
  const [userId, setUserId] = useState('');
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  const getInitialProcessingState = (id) => {
    axios
      .get(`${api}/user/task-status/${id}`)
      .then((res) => {
        console.log('task status single type', res.data);
        if (res.data.status === 'running') {
          setProcessing(true);
        } else {
          setProcessing(false);
        }
      })
      .catch((err) => {
        console.error('Failed to get task status:', err);
        setProcessing(false);
      });
  };

  const navigate = useNavigate();
  // Fetch user details
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const id = decoded.email;
        setUserId(id);
        axios
          .get(`${api}/user/details/${id}`)
          .then((res) => {
            if (res.data?.[0]) {
              setFormData({
                name: res.data[0].name || '',
                dob: res.data[0].dob || '',
                timeOfBirth: res.data[0].timeOfBirth || '',
                placeOfBirth: res.data[0].placeOfBirth || '',
              });
            }
            getInitialProcessingState(id);
          })
          .catch((err) => console.error('Failed to fetch user details:', err));
      } catch (err) {
        console.error('Invalid token:', err);
      }
    }
  }, []);

  // Save handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError('');
    try {
      const res = await axios.put(`${api}/user/userdetail/${userId}`, formData);
      dispatch(updateDetails(formData));
      console.log('Details saved!', res.data);
    } catch (err) {
      const msg = err.response?.data?.msg || 'Saving failed. Please try again.';
      setProcessing(false);
      setError(msg);
    }
  };

  useEffect(() => {
    let interval = null;
    console.log('processing', processing);
    if (processing != null && processing) {
      interval = setInterval(() => {
        axios
          .get(`${api}/user/task-status/${userId}`)
          .then((res) => {
            console.log('task status', res.data);
            if (res.data.status === 'completed') {
              setProcessing(false);
              clearInterval(interval);
              setError('');
            }
            if (res.data.error) {
              setError(res.data.error);
              setProcessing(false);
              clearInterval(interval);
            }
          })
          .catch((err) => {
            console.error('Failed to get task status:', err);
            setError('Failed to get task status');
            setProcessing(false);
            clearInterval(interval);
          });
      }, 5000);
    } else {
      if (interval) clearInterval(interval);
    }
  }, [processing, userId]);

  // Field setters
  const setDob = (newValue) => {
    setFormData((prev) => ({
      ...prev,
      dob: newValue ? dayjs(newValue).format('DD/MM/YYYY') : null,
    }));
  };

  const setTime = (newTime) => {
    setFormData((prev) => ({
      ...prev,
      timeOfBirth: newTime ? dayjs(newTime).format('HH:mm') : null,
    }));
  };

  const setPlace = (newPlace) => {
    setFormData((prev) => ({
      ...prev,
      placeOfBirth: newPlace,
    }));
  };

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <BreadcrumbNavbar />
        <ProcessingScreen isProcessing={processing}>
          <Box
            sx={{
              p: {
                xs: 2,
                sm: 4,
                md: 6,
                lg: 6,
              },
              px: 18,
            }}
          >
            {/* Title */}
            <Typography variant="h1" sx={{ marginBottom: '0px !important' }}>
              Birth Time Settings
            </Typography>

            {/* Tabs */}
            {/* <Tabs
                    value={activeTab}
                    onChange={(e, val) => setActiveTab(val)}
                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}
                  >
                    <Tab label="John" />
                  </Tabs> */}

            {/* Layout */}
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={0}
              alignItems="flex-start"
            >
              {/* Left form section */}
              <Stack spacing={4} sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 4, maxWidth: '500px' }}
                  color="warning"
                >
                  Processing will take time....Please be patient as each detail
                  change triggers a full recalculation for precise results.
                </Typography>
                {/* Name */}
                <StyledTextField
                  type="text"
                  variant="outlined"
                  label="Name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                />

                <StyledDatePicker
                  label="Date of Birth"
                  value={
                    formData.dob ? dayjs(formData.dob, 'DD/MM/YYYY') : null
                  }
                  disableFuture
                  onChange={setDob}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { placeholder: 'DD/MM/YYYY' } }}
                />
                <StyledTimePicker
                  label="Time of Birth"
                  value={
                    formData.timeOfBirth
                      ? dayjs(formData.timeOfBirth, 'HH:mm')
                      : null
                  }
                  minutesStep={1}
                  views={['hours', 'minutes']}
                  onChange={setTime}
                  ampm
                  slotProps={{
                    textField: { placeholder: '12:00 AM' },
                    // actionBar is the slot that renders the Accept/Cancel buttons
                    actionBar: {
                      sx: {
                        // target the buttons inside the action bar
                        '& .MuiButton-root': {
                          color: theme.palette.getContrastText(
                            theme.palette.primary.main
                          ),
                          backgroundColor: 'transparent',
                          borderRadius: 1,
                          textTransform: 'none',
                        },
                      },
                    },
                  }}
                />

                {/* Place */}
                <CustomLocationAutocomplete
                  onSelect={(place) => {
                    setPlace(`${place.lat},${place.lng}`);
                  }}
                  defaultLat={formData.placeOfBirth.split(',')[0] || null}
                  defaultLng={formData.placeOfBirth.split(',')[1] || null}
                />

                {error && (
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                )}

                <StyledButton
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    mt: 2,
                    bgcolor: '#000',
                    color: '#fff',
                    borderRadius: '50px',
                    px: 4,
                    py: 1.5,
                    '&:hover': { bgcolor: '#333' },
                  }}
                >
                  Save
                </StyledButton>
              </Stack>

              {/* Right section with image */}
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="flex-start"
                spacing={2}
                sx={{ flex: 1, textAlign: 'center' }}
              >
                <img
                  src={SettingImage}
                  alt="Astrology"
                  style={{ width: '100%', maxWidth: '450px' }}
                />
                <Typography
                  variant="body2"
                  sx={{ mt: 2, fontWeight: 'bold', cursor: 'pointer' }}
                  onClick={() =>
                    navigate('/home/Community_&_Impact/We_are_Different')
                  }
                >
                  How we are Different{' '}
                  <span style={{ fontWeight: 'bold' }}>?</span>
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </ProcessingScreen>
      </LocalizationProvider>
    </>
  );
};

export default SettingsForm;
